import React, {useCallback, useEffect, useRef, useState} from "react";
import {getNotificationsSummary, listNotifications, markNotificationRead} from "../api";
import type {NotificationItem, NotificationsSummary} from "../api";
import {useAuth} from "../auth";
import {navigate} from "../hooks";
import {formatDate} from "../utils";

// Поллинг сводки/активных алертов раз в минуту (без WS — осознанно).
const POLL_INTERVAL_MS = 60_000;
// Сколько активных уведомлений тянем для баннера и popover.
const ACTIVE_PREVIEW_LIMIT = 20;

/** Человекочитаемая метка типа алерта (используется и на странице мерчанта). */
export function notificationTypeLabel(type: string): string {
    switch (type) {
        case "merchant_overdraft_exceeded":
            return "Долг превысил лимит";
        case "merchant_low_balance":
            return "Баланс ушёл в овердрафт";
        case "chain_watcher_stale":
            return "TRC20-сканер молчит";
        case "chain_provider_quota_near_limit":
            return "Квота TronGrid на исходе";
        case "chain_reconciliation_recovered":
            return "TRC20: пропуск дозачислен";
        case "chain_reconciliation_mismatch":
            return "TRC20: подтверждённое расхождение";
        case "extsource_automation_blocked":
            // Останавливается заявка, а не кабинет: аккаунтный останов снят
            // 11.08.2026, и подпись про «автоматику аккаунта» с тех пор врала.
            return "Paylord: автоматика заявки остановлена";
        default:
            return type;
    }
}

/**
 * Алерт контура Paylord: его место в интерфейсе — колокольчик, а не баннер.
 * Гаснет он сам, когда заявка закрыта и разбирать по ней нечего.
 */
function isExternalIncident(item: NotificationItem): boolean {
    return item.type === "extsource_automation_blocked";
}

/**
 * Что именно показывать красной полосой поверх админки — и показывать ли
 * вообще. Вынесено из компонента, потому что это правило, а не вёрстка: его
 * проверяет тест, а не глаз.
 *
 * Прочитанное полосой не показывается. Крестик на полосе помечает уведомление
 * прочитанным на сервере, поэтому закрытая полоса остаётся закрытой и после
 * перезагрузки страницы: до этой правки признак жил в стейте компонента, и
 * та же полоса возвращалась при каждом обновлении.
 */
export function selectBannerNotification(items: NotificationItem[]): NotificationItem | null {
    return items.find(
        (item) => item.severity === "critical" && !isExternalIncident(item) && !item.readAt,
    ) ?? null;
}

export interface AdminNotificationsState {
    summary: NotificationsSummary;
    active: NotificationItem[];
    loading: boolean;
    error: unknown;
    refresh: () => void;
    markRead: (id: string) => void;
}

/**
 * Состояние центра уведомлений админки: сводка (счётчики) + список активных
 * алертов, поллинг раз в 60 сек. Живёт в AdminLayout, поэтому поллинг активен
 * только у админа. Интервал чистится на unmount.
 */
export function useAdminNotifications(): AdminNotificationsState {
    const {token} = useAuth();
    const [summary, setSummary] = useState<NotificationsSummary>({active: 0, unread: 0});
    const [active, setActive] = useState<NotificationItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<unknown>(null);
    const mounted = useRef(true);

    const refresh = useCallback(() => {
        if (!token) {
            setSummary({active: 0, unread: 0});
            setActive([]);
            return;
        }
        setLoading(true);
        Promise.all([
            getNotificationsSummary(token),
            listNotifications(token, {status: "active", limit: ACTIVE_PREVIEW_LIMIT}),
        ])
            .then(([summaryResp, listResp]) => {
                if (!mounted.current) return;
                setSummary(summaryResp);
                setActive(listResp.items);
                setError(null);
            })
            .catch((err) => {
                if (!mounted.current) return;
                setError(err);
            })
            .finally(() => {
                if (mounted.current) setLoading(false);
            });
    }, [token]);

    useEffect(() => {
        mounted.current = true;
        refresh();
        const id = window.setInterval(refresh, POLL_INTERVAL_MS);
        return () => {
            mounted.current = false;
            window.clearInterval(id);
        };
    }, [refresh]);

    const markRead = useCallback((id: string) => {
        if (!token) return;
        // Оптимистично гасим счётчик непрочитанного, потом сверяемся поллингом.
        setSummary((prev) => ({...prev, unread: Math.max(0, prev.unread - 1)}));
        setActive((prev) =>
            prev.map((item) => (item.id === id && !item.readAt ? {...item, readAt: new Date().toISOString()} : item)),
        );
        markNotificationRead(token, id)
            .then(() => refresh())
            .catch(() => refresh());
    }, [token, refresh]);

    return {summary, active, loading, error, refresh, markRead};
}

interface NotificationBellProps {
    state: AdminNotificationsState;
}

/** Пункт «Уведомления» в сайдбаре: иконка-колокольчик + бейдж unread + popover. */
export function NotificationBell({state}: NotificationBellProps): JSX.Element {
    const {summary, active, markRead} = state;
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onDocClick = (event: MouseEvent): void => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [open]);

    const openMerchant = (item: NotificationItem): void => {
        if (!item.readAt) markRead(item.id);
        setOpen(false);
        if (item.merchantId) {
            navigate(`#/admin/merchants/${item.merchantId}`);
        }
    };

    // Кнопок разбора здесь нет намеренно (решение владельца 15.08.2026):
    // уведомление читают, а не заполняют. Инцидент контура гаснет сам, когда его
    // заявка закрыта, — раньше закрыть его можно было только двумя диалогами
    // подряд, с вопросом про то, кто получил сообщение в Telegram.
    return (
        <div className="notification-center" ref={containerRef}>
            <button
                type="button"
                className={`nav-item notification-bell ${open ? "active" : ""}`}
                onClick={() => setOpen((prev) => !prev)}
                aria-label="Notifications"
            >
                Notifications
                {summary.unread > 0 && (
                    <span className="notification-badge">{summary.unread > 99 ? "99+" : summary.unread}</span>
                )}
            </button>
            {open && (
                <div className="notification-popover" role="menu">
                    <div className="notification-popover-head">
                        <strong>Активные уведомления</strong>
                        <span className="notification-popover-count">{summary.active}</span>
                    </div>
                    <div className="notification-popover-list">
                        {active.length === 0 && (
                            <div className="empty-state compact">Активных уведомлений нет.</div>
                        )}
                        {active.map((item) => (
                            <div key={item.id} className="notification-popover-entry">
                                <button
                                    type="button"
                                    className={`notification-popover-item ${item.readAt ? "read" : "unread"}`}
                                    onClick={() => openMerchant(item)}
                                >
                                    <span className={`notification-dot severity-${item.severity}`} aria-hidden="true"/>
                                    <span className="notification-popover-text">
                                        <span className="notification-popover-title">{item.title}</span>
                                        <span className="notification-popover-meta">
                                            {notificationTypeLabel(item.type)} · {formatDate(item.createdAt)}
                                        </span>
                                    </span>
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

interface NotificationCriticalBannerProps {
    state: AdminNotificationsState;
}

/**
 * Топ-баннер поверх контента при наличии непрочитанного critical-алерта.
 * Стиль — по образцу ErrorBanner (alert-error).
 *
 * Крестик помечает уведомление прочитанным на сервере, а не прячет его в стейте
 * компонента: закрытая полоса обязана остаться закрытой после перезагрузки
 * страницы (решение владельца 15.08.2026). Раньше признак жил в памяти
 * компонента, и алерт от 13.08 всплывал вторые сутки на каждом F5. Само
 * уведомление при этом никуда не девается — оно остаётся в колокольчике.
 *
 * Алерты контура Paylord баннером не показываются (решение владельца
 * 11.08.2026): на тестовом прогоне каждая заявка, закрытая в кабинете, давала
 * красную полосу поверх админки. Сам алерт никуда не делся — он в колокольчике,
 * в счётчике и в Telegram. Отфильтровано по типу, а не понижением severity:
 * severity управляет маршрутизацией инцидента, и менять её ради вёрстки значит
 * менять смысл алерта.
 */
export function NotificationCriticalBanner({state}: NotificationCriticalBannerProps): JSX.Element | null {
    const {active, markRead} = state;

    const critical = selectBannerNotification(active);
    if (!critical) return null;

    return (
        <div className="alert alert-error notification-critical-banner" role="alert">
            <span>
                <strong>{critical.title}.</strong> {critical.body}
            </span>
            <span className="notification-critical-actions">
                {critical.merchantId && (
                    <a
                        className="notification-critical-link"
                        href={`#/admin/merchants/${critical.merchantId}`}
                    >
                        К мерчанту
                    </a>
                )}
                <button
                    type="button"
                    className="alert-dismiss"
                    aria-label="Скрыть"
                    onClick={() => markRead(critical.id)}
                >
                    ×
                </button>
            </span>
        </div>
    );
}
